import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import Footer from './components/Footer';
import AdminPage from './components/AdminPage';
import AdminLogin from './components/AdminLogin';
import { defaultContent } from './data/siteData';

// Lazy load components for better performance
const LazyStudioSection = lazy(() => import('./components/StudioSection'));
const LazyExpertiseSection = lazy(() => import('./components/ExpertiseSection'));
const LazyBuiltDifferent = lazy(() => import('./components/BuiltDifferent'));
const LazyTestimonials = lazy(() => import('./components/Testimonials'));
const LazyPortfolio = lazy(() => import('./components/Portfolio'));
const LazyFinalCta = lazy(() => import('./components/FinalCTA'));

// Loading fallback component
const SectionFallback = () => <div style={{ height: '100vh', background: 'var(--bg-dark)' }} />;

const STORAGE_KEY = 'cimprints_admin';
const ADMIN_AUTH_KEY = 'cimprints_admin_auth';
const ADMIN_EMAIL = 'admin@cimprints.com';
const ADMIN_PASSWORD = 'admin123@@';
const API_URL = process.env.REACT_APP_API_URL || '/api/content';
const FALLBACK_API_URL = 'https://cimprintbackend.onrender.com/api/content';

function buildApiCandidates(preferredUrl) {
  const candidates = [];

  if (preferredUrl) {
    candidates.push(preferredUrl);
  }

  if (API_URL && !candidates.includes(API_URL)) {
    candidates.push(API_URL);
  }

  if (!candidates.includes('/api/content')) {
    candidates.push('/api/content');
  }

  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    const localApi = 'http://localhost:4000/api/content';
    if (!candidates.includes(localApi)) {
      candidates.push(localApi);
    }
  }

  if (!candidates.includes(FALLBACK_API_URL)) {
    candidates.push(FALLBACK_API_URL);
  }

  return candidates;
}

async function requestFirstSuccessfulJson(candidates, init = {}) {
  let lastError = null;

  for (const url of candidates) {
    try {
      const response = await fetch(url, init);
      if (!response.ok) {
        lastError = new Error(`HTTP ${response.status} from ${url}`);
        continue;
      }

      const data = await response.json();
      return { url, data };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('No API endpoint is reachable.');
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function mergeDeep(base, override) {
  if (Array.isArray(base)) {
    return Array.isArray(override) ? deepClone(override) : deepClone(base);
  }

  if (base && typeof base === 'object') {
    const result = deepClone(base);
    if (override && typeof override === 'object') {
      Object.keys(override).forEach((key) => {
        if (base[key] && typeof base[key] === 'object' && !Array.isArray(base[key]) && override[key] && typeof override[key] === 'object' && !Array.isArray(override[key])) {
          result[key] = mergeDeep(base[key], override[key]);
        } else {
          result[key] = deepClone(override[key]);
        }
      });
    }

    return result;
  }

  return override !== undefined ? deepClone(override) : deepClone(base);
}

function buildContentStore(rawContent) {
  const content = rawContent && typeof rawContent === 'object' ? rawContent : {};
  const normalizeSharedPortfolioFields = (store) => {
    const enItems = store.en?.portfolio?.items;
    const arItems = store.ar?.portfolio?.items;

    if (!Array.isArray(enItems) || !Array.isArray(arItems)) {
      return store;
    }

    const normalizedArItems = arItems.map((item, index) => {
      const englishItem = enItems[index] || {};
      return {
        ...item,
        image: englishItem.image ?? item.image,
        imageData: englishItem.imageData ?? item.imageData,
        link: englishItem.link ?? item.link,
        gradient: englishItem.gradient ?? item.gradient,
      };
    });

    return {
      ...store,
      ar: {
        ...store.ar,
        portfolio: {
          ...store.ar.portfolio,
          items: normalizedArItems,
        },
      },
    };
  };

  if (content.en || content.ar) {
    return normalizeSharedPortfolioFields({
      en: mergeDeep(deepClone(defaultContent.en), content.en || {}),
      ar: mergeDeep(deepClone(defaultContent.ar), content.ar || {})
    });
  }

  return normalizeSharedPortfolioFields(deepClone(defaultContent));
}

function parseStoredContent(savedValue) {
  if (!savedValue) {
    return { content: deepClone(defaultContent), updatedAtMs: 0 };
  }

  try {
    const parsed = JSON.parse(savedValue);

    if (parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'content')) {
      const updatedAtMs = parsed.updatedAt ? Date.parse(parsed.updatedAt) || 0 : 0;

      return {
        content: buildContentStore(parsed.content),
        updatedAtMs,
      };
    }

    return {
      content: buildContentStore(parsed),
      updatedAtMs: 0,
    };
  } catch (error) {
    console.error('Failed to parse saved content:', error);
    return { content: deepClone(defaultContent), updatedAtMs: 0 };
  }
}

function loadInitialContentState() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) {
    return { content: deepClone(defaultContent), updatedAtMs: 0 };
  }

  return parseStoredContent(saved);
}

function persistContentState(content, updatedAtMs) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        content,
        updatedAt: updatedAtMs ? new Date(updatedAtMs).toISOString() : null,
      })
    );
  } catch (error) {
    // Ignore storage errors.
  }
}

function AppContent({ lang, setLang, content, setContent, isAdminAuthenticated, onLogin, onLogout, loginError, syncStatus, activeApiUrl }) {
  const location = useLocation();
  const isAdminRoute = location.pathname === '/admin';
  const fallbackPath = new URLSearchParams(location.search).get('path');
  const toggleLang = () => setLang(lang === 'en' ? 'ar' : 'en');

  return (
    <div className="App" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {!isAdminRoute && <Navbar lang={lang} copy={content.navbar} toggleLang={toggleLang} />}
      <Routes>
        <Route path="/" element={<Navigate to={fallbackPath || `/${lang}`} replace />} />
        <Route path="/:lang" element={
          !isAdminRoute ? (
            <>
              <HeroSection copy={content.hero} />
              <Suspense fallback={<SectionFallback />}>
                <LazyStudioSection copy={content.studio} />
              </Suspense>
              <Suspense fallback={<SectionFallback />}>
                <LazyExpertiseSection copy={content.expertise} />
              </Suspense>
              <Suspense fallback={<SectionFallback />}>
                <LazyBuiltDifferent copy={content.builtDifferent} />
              </Suspense>
              <Suspense fallback={<SectionFallback />}>
                <LazyTestimonials copy={content.testimonials} />
              </Suspense>
              <Suspense fallback={<SectionFallback />}>
                <LazyPortfolio copy={content.portfolio} />
              </Suspense>
              <Suspense fallback={<SectionFallback />}>
                <LazyFinalCta copy={content.finalCta} />
              </Suspense>
              <Footer copy={content.footer} />
            </>
          ) : null
        } />
        <Route
          path="/admin"
          element={
            isAdminAuthenticated ? (
              <AdminPage
                lang={lang}
                toggleLang={toggleLang}
                content={content}
                onContentUpdate={setContent}
                storageKey="cimprints_admin"
                onLogout={onLogout}
                syncStatus={syncStatus}
                activeApiUrl={activeApiUrl}
              />
            ) : (
              <AdminLogin onLogin={onLogin} error={loginError} />
            )
          }
        />
      </Routes>
    </div>
  );
}

function App() {
  const [lang, setLang] = useState('en');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(() => localStorage.getItem(ADMIN_AUTH_KEY) === 'true');
  const [loginError, setLoginError] = useState('');

  // contentStore holds per-language copies, e.g. { en: {...}, ar: {...} }
  const initialContentState = loadInitialContentState();
  const [contentStore, setContentStore] = useState(() => initialContentState.content);
  const localContentUpdatedAtRef = useRef(initialContentState.updatedAtMs);
  const activeApiUrlRef = useRef(API_URL);
  const [activeApiUrl, setActiveApiUrl] = useState(API_URL);
  const [syncStatus, setSyncStatus] = useState({ state: 'idle', message: '' });

  useEffect(() => {
    let active = true;

    const syncRemoteContent = async () => {
      try {
        const { url, data: snapshot } = await requestFirstSuccessfulJson(
          buildApiCandidates(activeApiUrlRef.current),
          { headers: { Accept: 'application/json' } }
        );

        activeApiUrlRef.current = url;
        if (activeApiUrl !== url) {
          setActiveApiUrl(url);
        }

        if (!snapshot || !snapshot.content) {
          return;
        }

        const remoteUpdatedAtMs = snapshot.updatedAt ? Date.parse(snapshot.updatedAt) || 0 : 0;
        if (localContentUpdatedAtRef.current && remoteUpdatedAtMs < localContentUpdatedAtRef.current) {
          return;
        }

        const merged = buildContentStore(snapshot.content);
        if (!active) {
          return;
        }

        setContentStore(merged);
        localContentUpdatedAtRef.current = remoteUpdatedAtMs || Date.now();
        persistContentState(merged, localContentUpdatedAtRef.current);
        setSyncStatus({ state: 'online', message: 'Connected to content server.' });
      } catch (error) {
        // Keep local state if the API is unavailable.
        setSyncStatus({
          state: 'offline',
          message: 'Could not reach content server. This browser may show unsynced content.'
        });
      }
    };

    void syncRemoteContent();

    return () => {
      active = false;
    };
  }, [activeApiUrl]);

  const content = contentStore[lang] || defaultContent[lang];

  // Update current language content and persist to localStorage
  const setContent = (nextContent) => {
    setContentStore((prev) => {
      const updated = buildContentStore({ ...(prev || defaultContent), [lang]: nextContent });
      const portfolioCopy = deepClone(nextContent?.portfolio || defaultContent[lang]?.portfolio || {});

      updated.en = {
        ...updated.en,
        portfolio: deepClone(portfolioCopy),
      };

      updated.ar = {
        ...updated.ar,
        portfolio: deepClone(portfolioCopy),
      };

      localContentUpdatedAtRef.current = Date.now();

      persistContentState(updated, localContentUpdatedAtRef.current);
      setSyncStatus({ state: 'saving', message: 'Saving to content server...' });

      void requestFirstSuccessfulJson(buildApiCandidates(activeApiUrlRef.current), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: updated })
      })
        .then(({ url, data }) => {
          activeApiUrlRef.current = url;
          if (activeApiUrl !== url) {
            setActiveApiUrl(url);
          }

          const remoteUpdatedAtMs = data?.updatedAt ? Date.parse(data.updatedAt) || 0 : 0;
          if (remoteUpdatedAtMs) {
            localContentUpdatedAtRef.current = remoteUpdatedAtMs;
            persistContentState(updated, remoteUpdatedAtMs);
          }

          setSyncStatus({ state: 'saved', message: 'Saved and synced across devices.' });
        })
        .catch(() => {
          setSyncStatus({
            state: 'save-error',
            message: 'Saved only in this browser. API server is unreachable or blocked by CORS.'
          });
          // Keep the local save even if the backend is temporarily offline.
        });

      return updated;
    });
  };

  const handleLogin = (email, password) => {
    if (email.trim().toLowerCase() === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
      setIsAdminAuthenticated(true);
      setLoginError('');
      localStorage.setItem(ADMIN_AUTH_KEY, 'true');
      return true;
    }

    setIsAdminAuthenticated(false);
    setLoginError('Invalid admin credentials.');
    localStorage.removeItem(ADMIN_AUTH_KEY);
    return false;
  };

  const handleLogout = () => {
    setIsAdminAuthenticated(false);
    localStorage.removeItem(ADMIN_AUTH_KEY);
  };

  // Custom cursor effect - only on desktop
  useEffect(() => {
    if (window.matchMedia('(max-width: 768px)').matches) {
      return; // Skip cursor effect on mobile
    }

    const cursor = document.createElement('div');
    cursor.className = 'custom-cursor';
    document.body.appendChild(cursor);
    
    const move = (e) => {
      cursor.style.left = e.clientX + 'px';
      cursor.style.top = e.clientY + 'px';
    };
    
    window.addEventListener('mousemove', move);
    
    return () => {
      window.removeEventListener('mousemove', move);
      cursor.remove();
    };
  }, []);

  return (
    <BrowserRouter>
      <AppContent
        lang={lang}
        setLang={setLang}
        content={content}
        setContent={setContent}
        isAdminAuthenticated={isAdminAuthenticated}
        onLogin={handleLogin}
        onLogout={handleLogout}
        loginError={loginError}
        syncStatus={syncStatus}
        activeApiUrl={activeApiUrl}
      />
    </BrowserRouter>
  );
}

export default App;